import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { addDays, format, parseISO, subDays } from 'date-fns';
import { FaCalendarAlt, FaChevronLeft, FaChevronRight, FaRedo, FaSave } from 'react-icons/fa';
import API_BASE_URL, {
  getAdminAuthHeaders,
  handleAdminAuthError,
  isAdminAuthenticated,
} from '../api';

const formatDate = (date) => format(date, 'yyyy-MM-dd');
const parseDateInput = (value) => parseISO(value);
const cellKey = (item) => String(item.id);

const Inventory = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [draftValues, setDraftValues] = useState({});
  const [dirtyCells, setDirtyCells] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');

  const calendarRef = useRef();

  const weekDates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(selectedDate, i)),
    [selectedDate]
  );

  const fetchInventory = useCallback(async () => {
    if (!isAdminAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    setLoading(true);
    setError('');
    setSavedMessage('');

    try {
      const response = await axios.get(`${API_BASE_URL}/inventory`, {
        headers: getAdminAuthHeaders(),
        params: {
          startDate: formatDate(weekDates[0]),
          endDate: formatDate(weekDates[weekDates.length - 1]),
        },
      });

      const rows = response.data || [];
      setInventory(rows);
      setDraftValues(
        rows.reduce((acc, item) => {
          acc[cellKey(item)] = String(item.available_rooms);
          return acc;
        }, {})
      );
      setDirtyCells({});
    } catch (fetchError) {
      if (handleAdminAuthError(fetchError, navigate)) return;
      setError(fetchError.response?.data?.message || 'Failed to load inventory.');
    } finally {
      setLoading(false);
    }
  }, [navigate, weekDates]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const groupedRows = useMemo(() => {
    const grouped = inventory.reduce((acc, item) => {
      if (!acc[item.room_id]) {
        acc[item.room_id] = {
          roomId: item.room_id,
          roomName: item.room?.name || `Room ${item.room_id}`,
          maxPerson: item.room?.maxPerson,
          items: [],
        };
      }
      acc[item.room_id].items.push(item);
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => Number(a.roomId) - Number(b.roomId));
  }, [inventory]);

  const dirtyCount = Object.keys(dirtyCells).length;

  const totalsByDate = useMemo(() => {
    return weekDates.map((date) => {
      const dateKey = formatDate(date);
      return inventory.reduce((total, item) => {
        if (item.date !== dateKey) return total;
        const value = Number(draftValues[cellKey(item)]);
        return total + (Number.isFinite(value) ? value : 0);
      }, 0);
    });
  }, [draftValues, inventory, weekDates]);

  const soldOutCount = useMemo(() => {
    return inventory.reduce((count, item) => {
      const value = Number(draftValues[cellKey(item)]);
      return count + (value === 0 ? 1 : 0);
    }, 0);
  }, [draftValues, inventory]);

  const totalAvailable = totalsByDate.reduce((sum, value) => sum + value, 0);

  const handleDateSelect = (value) => {
    if (!value) return;
    setSelectedDate(parseDateInput(value));
    setCalendarOpen(false);
  };

  const handleCellChange = (item, value) => {
    if (!/^\d*$/.test(value)) return;

    const key = cellKey(item);
    setDraftValues((prev) => ({ ...prev, [key]: value }));
    setDirtyCells((prev) => {
      const next = { ...prev };
      if (value === String(item.available_rooms)) {
        delete next[key];
      } else {
        next[key] = item;
      }
      return next;
    });
    setSavedMessage('');
  };

  const discardChanges = () => {
    setDraftValues(
      inventory.reduce((acc, item) => {
        acc[cellKey(item)] = String(item.available_rooms);
        return acc;
      }, {})
    );
    setDirtyCells({});
    setSavedMessage('');
    setError('');
  };

  const saveChanges = async () => {
    const changedItems = Object.values(dirtyCells);
    if (changedItems.length === 0) return;

    const invalid = changedItems.some((item) => {
      const value = Number(draftValues[cellKey(item)]);
      return !Number.isInteger(value) || value < 0;
    });

    if (invalid) {
      setError('Availability must be a non-negative whole number.');
      return;
    }

    const updatesByDate = changedItems.reduce((acc, item) => {
      acc[item.date] = acc[item.date] || [];
      acc[item.date].push({
        room_id: item.room_id,
        available_rooms: Number(draftValues[cellKey(item)]),
      });
      return acc;
    }, {});

    setSaving(true);
    setError('');
    setSavedMessage('');

    try {
      await Promise.all(
        Object.entries(updatesByDate).map(([date, updates]) =>
          axios.post(
            `${API_BASE_URL}/inventory/bulk-update`,
            { date, updates },
            { headers: getAdminAuthHeaders() }
          )
        )
      );

      await fetchInventory();
      setSavedMessage('Inventory saved successfully.');
    } catch (saveError) {
      if (handleAdminAuthError(saveError, navigate)) return;
      setError(saveError.response?.data?.message || 'Failed to save inventory changes.');
    } finally {
      setSaving(false);
    }
  };

  const getCellItem = (items, date) => {
    const dateKey = formatDate(date);
    return items.find((item) => item.date === dateKey);
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 text-slate-900 md:p-6">
      <div className="mb-5 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">
            Bulk Editor
          </p>
          <h1 className="mt-1 text-3xl font-bold">Inventory Management</h1>
          <p className="mt-1 text-sm text-slate-600">
            Edit weekly room availability, then save all changes together.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setSelectedDate((date) => subDays(date, 7))}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-slate-50"
          >
            <FaChevronLeft /> Previous
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate(new Date())}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-slate-50"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate((date) => addDays(date, 7))}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-slate-50"
          >
            Next <FaChevronRight />
          </button>

          <div className="relative" ref={calendarRef}>
            <button
              type="button"
              onClick={() => setCalendarOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
            >
              <FaCalendarAlt /> {format(weekDates[0], 'dd MMM')} -{' '}
              {format(weekDates[6], 'dd MMM')}
            </button>
            {calendarOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 rounded-md border bg-white p-3 shadow-lg">
                <input
                  type="date"
                  className="rounded border border-slate-300 p-2"
                  value={formatDate(selectedDate)}
                  onChange={(e) => handleDateSelect(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Total Available This Week</p>
          <p className="mt-1 text-3xl font-bold">{totalAvailable}</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Sold-Out Cells</p>
          <p className="mt-1 text-3xl font-bold">{soldOutCount}</p>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Unsaved Changes</p>
          <p className="mt-1 text-3xl font-bold">{dirtyCount}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p>}
          {savedMessage && (
            <p className="rounded-md bg-green-50 px-3 py-2 text-sm font-medium text-green-700">
              {savedMessage}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={discardChanges}
            disabled={dirtyCount === 0 || saving}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaRedo /> Discard
          </button>
          <button
            type="button"
            onClick={saveChanges}
            disabled={dirtyCount === 0 || saving}
            className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaSave /> {saving ? 'Saving...' : `Save Changes${dirtyCount ? ` (${dirtyCount})` : ''}`}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-[980px] w-full border-collapse text-sm">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="sticky left-0 z-10 w-64 bg-slate-800 p-3 text-left font-semibold">
                  Room
                </th>
                {weekDates.map((date, index) => (
                  <th key={formatDate(date)} className="p-3 text-center font-semibold">
                    <span className="block">{format(date, 'EEE')}</span>
                    <span className="text-xs font-normal text-slate-300">
                      {format(date, 'dd MMM')}
                    </span>
                    <span className="mt-1 block text-xs text-blue-200">
                      Total {totalsByDate[index] || 0}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-slate-500">
                    Loading inventory...
                  </td>
                </tr>
              ) : groupedRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-slate-500">
                    No inventory rows found for this week.
                  </td>
                </tr>
              ) : (
                groupedRows.map((row) => (
                  <tr key={row.roomId} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="sticky left-0 z-10 border-r border-slate-200 bg-white p-3">
                      <p className="font-semibold">{row.roomName}</p>
                      <p className="text-xs text-slate-500">
                        Room ID {row.roomId}
                        {row.maxPerson ? ` | Max ${row.maxPerson}` : ''}
                      </p>
                    </td>
                    {weekDates.map((date) => {
                      const item = getCellItem(row.items, date);
                      if (!item) {
                        return (
                          <td key={formatDate(date)} className="p-3 text-center text-slate-400">
                            -
                          </td>
                        );
                      }

                      const key = cellKey(item);
                      const value = draftValues[key] ?? '';
                      const isDirty = Boolean(dirtyCells[key]);
                      const numericValue = Number(value);
                      const isSoldOut = value !== '' && numericValue === 0;

                      return (
                        <td key={formatDate(date)} className="p-3 text-center">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={value}
                            onChange={(e) => handleCellChange(item, e.target.value)}
                            className={`h-10 w-20 rounded-md border px-2 text-center font-semibold outline-none transition ${
                              isDirty
                                ? 'border-amber-400 bg-amber-50 text-amber-900'
                                : isSoldOut
                                  ? 'border-red-200 bg-red-50 text-red-700'
                                  : 'border-slate-300 bg-white text-slate-900'
                            } focus:border-blue-500 focus:ring-2 focus:ring-blue-100`}
                            aria-label={`${row.roomName} availability for ${format(date, 'dd MMM')}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Inventory;
